import { msg } from '@lingui/core/macro'
import type { StatusLightToken } from '@oranje/ui'

import { labelMap } from '@/shared/lib/i18nLabels'

/**
 * Semáforo de Onboarding: estados, transiciones válidas y cómo se pintan.
 *
 * ⚠ ESTE ARCHIVO ESTÁ EN EL LUGAR EQUIVOCADO A PROPÓSITO.
 *
 * Según Estructura de Proyecto §6 los semáforos viven en `packages/domain/src/statusLights/`, que es la
 * fuente de verdad en código, y `packages/domain` hoy está vacío y fuera del
 * alcance de este trabajo (solo se toca `apps/web`). Queda aquí para no
 * bloquear las pantallas, y debe migrarse tal cual a
 * `packages/domain/src/statusLights/onboardingStatusLight.ts` en cuanto se pueda
 * tocar ese paquete.
 *
 * Vive en `shared/constants/` y no dentro de una feature porque lo consumen
 * `onboarding` y `territory`, y §4 prohíbe que una feature importe de otra.
 *
 * Códigos y transiciones copiados del seed de la API (`apps/api/prisma/seed.ts`,
 * semáforo ONBOARDING): son los que `catalogs.status_light_state` acepta y los
 * que viajan por HTTP. Un cambio allá obliga a cambiar aquí.
 */
export const OnboardingStatus = {
  GRAY: 'GRAY',
  LIGHT_BLUE: 'LIGHT_BLUE',
  GREEN: 'GREEN',
  YELLOW: 'YELLOW',
  PINK: 'PINK',
  BROWN: 'BROWN',
  ORANGE: 'ORANGE',
  RED: 'RED',
  BLACK: 'BLACK',
} as const

export type OnboardingStatus = (typeof OnboardingStatus)[keyof typeof OnboardingStatus]

/**
 * Los nombres de color se quedan en español porque son los del token
 * (`--st-azul-claro`) y §5 dice que los tokens no se renombran. Lo que va en
 * inglés es todo lo demás del dominio (`Prospect`, `status`, `owner`).
 */
export const ONBOARDING_STATUS_TOKEN: Record<OnboardingStatus, StatusLightToken> = {
  GRAY: 'st-gris',
  LIGHT_BLUE: 'st-azul-claro',
  GREEN: 'st-verde',
  YELLOW: 'st-amarillo',
  PINK: 'st-rosa',
  BROWN: 'st-cafe',
  ORANGE: 'st-naranja',
  RED: 'st-rojo',
  BLACK: 'st-negro',
} as const

/** Texto del chip. Es el nombre del color, tal como aparece en el diseño. */
const ONBOARDING_STATUS_MESSAGE = {
  GRAY: msg`Gris`,
  LIGHT_BLUE: msg`Azul claro`,
  GREEN: msg`Verde`,
  YELLOW: msg`Amarillo`,
  PINK: msg`Rosa`,
  BROWN: msg`Cafe`,
  ORANGE: msg`Naranja`,
  RED: msg`Rojo`,
  BLACK: msg`Negro`,
}

export const ONBOARDING_STATUS_LABEL: Record<OnboardingStatus, string> =
  labelMap(ONBOARDING_STATUS_MESSAGE)

/**
 * Qué significa el color en ESTE semáforo. El mismo color dice otra cosa en
 * los otros.
 *
 * GREEN ya NO repite el nombre oficial "Propuesta enviada" (el de la base,
 * que sigue siendo ese — ver `ONBOARDING_TRANSITION_TITLE`): como leyenda
 * fija de columna/mapa/ayuda se leía igual de confuso que en el modal de
 * transición, un prospecto recién llegado a Verde no tiene nada enviado
 * todavía.
 */
const ONBOARDING_STATUS_DESCRIPTION_MESSAGE = {
  GRAY: msg`Hotel identificado`,
  LIGHT_BLUE: msg`Contacto y datos`,
  GREEN: msg`Armar y enviar propuesta`,
  YELLOW: msg`Seguimiento`,
  PINK: msg`Negociación de términos`,
  BROWN: msg`Renegociación / desbloqueo`,
  ORANGE: msg`Cliente activo`,
  RED: msg`Rechazo o no interés`,
  BLACK: msg`Cliente pausado o inactivo`,
}

export const ONBOARDING_STATUS_DESCRIPTION: Record<OnboardingStatus, string> = labelMap(
  ONBOARDING_STATUS_DESCRIPTION_MESSAGE,
)

/**
 * Título y descripción cuando el color aparece como DESTINO al elegir a dónde
 * mover el prospecto (`ChangeStatusDialog`). Solo se sobreescriben donde el
 * nombre oficial del estado —que describe cómo se SALE de él, no cómo se
 * entra (Semáforo Onboarding del vault)— confundiría como si ya fuera un
 * requisito cumplido: "Verde — Propuesta enviada" se lee como que ya se
 * envió, cuando en realidad es ahí donde se arma y se envía. Los demás
 * colores no tienen este problema (describen la fase o el motivo de entrada,
 * no una acción ya hecha) y se quedan con `ONBOARDING_STATUS_LABEL` /
 * `ONBOARDING_STATUS_DESCRIPTION`.
 */
const ONBOARDING_TRANSITION_TITLE_MESSAGE = {
  GREEN: msg`Vamos a crear la propuesta`,
}

export const ONBOARDING_TRANSITION_TITLE: Partial<Record<OnboardingStatus, string>> = labelMap(
  ONBOARDING_TRANSITION_TITLE_MESSAGE,
)

const ONBOARDING_TRANSITION_DESCRIPTION_MESSAGE = {
  GREEN: msg`Se arma y se envía aquí, dentro de Verde. No se sale hacia otro estado sin haberla mandado.`,
}

export const ONBOARDING_TRANSITION_DESCRIPTION: Partial<Record<OnboardingStatus, string>> =
  labelMap(ONBOARDING_TRANSITION_DESCRIPTION_MESSAGE)

/**
 * Las 12 aristas del seed (13 filas: `GREEN → BROWN` la autorizan BD o BDC).
 * Solo son referencia para los mocks: en pantalla las transiciones vienen de
 * `GET /prospects/:id/transitions`, ya filtradas por rol.
 */
export const ONBOARDING_TRANSITIONS: Record<OnboardingStatus, readonly OnboardingStatus[]> = {
  GRAY: ['LIGHT_BLUE'],
  LIGHT_BLUE: ['GREEN'],
  GREEN: ['YELLOW', 'RED', 'BROWN'],
  YELLOW: ['PINK'],
  PINK: ['ORANGE', 'BROWN'],
  ORANGE: ['BLACK'],
  /** RR-V-07: Azul claro es el ÚNICO punto de reentrada. */
  RED: ['LIGHT_BLUE'],
  BROWN: ['LIGHT_BLUE'],
  BLACK: ['LIGHT_BLUE'],
}

/**
 * Columnas del tablero, en orden. `ORANGE`, `RED` y `BLACK` quedan fuera: el
 * tablero es de prospectos ABIERTOS. Los convertidos se ven en Clientes
 * Activos y los rechazados no se arrastran por el pipeline.
 */
export const PIPELINE_COLUMNS: readonly OnboardingStatus[] = [
  'GRAY',
  'LIGHT_BLUE',
  'GREEN',
  'YELLOW',
  'PINK',
  'BROWN',
]

/**
 * De Amarillo en adelante el siguiente papel es el Contrato, y se arma con el
 * cuadro de la propuesta que el hotel ya vio (Hugo, 2026-10-16). Naranja NO
 * entra aquí: ese caso (sin contrato vigente) depende de un dato que esta
 * lista no tiene — `ProspectDetailPage` lo revisa aparte con
 * `proposals?.hasActiveContract`.
 */
export const CONTRACT_ELIGIBLE_STATUSES: ReadonlySet<OnboardingStatus> = new Set([
  'YELLOW',
  'PINK',
  'BROWN',
])

/**
 * Ya no queda como "sin transiciones": desde el seed TODOS los estados tienen
 * salida (RED, BROWN y BLACK reentran por Azul claro). Terminal aquí significa
 * que el prospecto salió del pipeline activo.
 */
const TERMINAL_STATUSES: readonly OnboardingStatus[] = ['ORANGE', 'RED', 'BLACK']

export function isTerminalStatus(status: OnboardingStatus): boolean {
  return TERMINAL_STATUSES.includes(status)
}
